// The blob. Three tiers, chosen inside: a WebGL2 raymarched SDF, a 2D canvas
// radial SDF, and a CSS gradient disc. The caller never learns which is running
// and never has to handle a failure — every path here degrades to a quieter
// version of the same character rather than to an error.

import type { BlobFrame, BlobState } from "./palette";
import { BlobMotion, linearToCss, paletteCss } from "./palette";
import { FRAG_SRC, UNIFORM_NAMES, VERT_SRC, ZOOM } from "./shaders";
import type { UniformName } from "./shaders";
import { CssBlob } from "./fallback";

export type { BlobState } from "./palette";

// MEASURED ceiling for 60fps on an Intel UHD iGPU. Above it the raymarched tier
// falls off a cliff, and a 1400 square draw killed the GPU process outright and
// blocklisted WebGL2 browser-wide for minutes. Cost scales with pixels, not with
// march steps, so this is the knob that matters.
const MAX_DEVICE_PX = 640;

/** Screen radius, as a fraction of the half short axis, for a world radius. */
function screenRadius(r: number): number {
  return r / (ZOOM * Math.sqrt(Math.max(12.25 - r * r, 1e-4)));
}

interface Painter {
  resize(w: number, h: number): void;
  paint(f: BlobFrame): void;
  destroy(): void;
}

// ---------------------------------------------------------------------------
// Tier 1: WebGL2
// ---------------------------------------------------------------------------

class WebglPainter implements Painter {
  private prog: WebGLProgram | null = null;
  private vao: WebGLVertexArrayObject | null = null;
  private uniforms = {} as Record<UniformName, WebGLUniformLocation | null>;
  private steps = 12;

  constructor(readonly gl: WebGL2RenderingContext) {
    this.build();
  }

  /** Throws if the program will not compile or link; the caller drops a tier. */
  build(): void {
    const gl = this.gl;
    const vs = this.compile(gl.VERTEX_SHADER, VERT_SRC);
    const fs = this.compile(gl.FRAGMENT_SHADER, FRAG_SRC);
    const prog = gl.createProgram();
    if (!prog) throw new Error("blob: createProgram failed");
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      const log = gl.getProgramInfoLog(prog) ?? "link failed";
      gl.deleteProgram(prog);
      throw new Error(`blob: ${log}`);
    }
    gl.useProgram(prog);
    // WebGL2 still requires a bound VAO even for an attribute-less draw.
    this.vao = gl.createVertexArray();
    gl.bindVertexArray(this.vao);
    for (const n of UNIFORM_NAMES) this.uniforms[n] = gl.getUniformLocation(prog, n);
    this.prog = prog;

    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.BLEND);
  }

  private compile(type: number, src: string): WebGLShader {
    const gl = this.gl;
    const sh = gl.createShader(type);
    if (!sh) throw new Error("blob: createShader failed");
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
      const log = gl.getShaderInfoLog(sh) ?? "compile failed";
      gl.deleteShader(sh);
      throw new Error(`blob: ${log}`);
    }
    return sh;
  }

  /** Fewer march steps when the frame budget is being missed. */
  setQuality(low: boolean): void {
    this.steps = low ? 8 : 12;
  }

  resize(w: number, h: number): void {
    this.gl.viewport(0, 0, w, h);
  }

  paint(f: BlobFrame): void {
    const gl = this.gl;
    const u = this.uniforms;
    if (!this.prog || gl.isContextLost()) return;
    gl.useProgram(this.prog);
    gl.bindVertexArray(this.vao);

    gl.uniform2f(u.uRes, gl.drawingBufferWidth, gl.drawingBufferHeight);
    gl.uniform1f(u.uTime, f.time);
    gl.uniform1f(u.uLevel, f.level);
    gl.uniform1i(u.uSOct, f.sOct);
    gl.uniform1i(u.uSteps, this.steps);
    gl.uniform1f(u.uR0, f.r0);
    // A zero-thickness shell degenerates the march interval; under reduced
    // motion the amplitude is nominally zero, so keep a hair of thickness.
    gl.uniform1f(u.uAmp, Math.max(f.r0 * f.ampFrac, 5e-4));
    gl.uniform1f(u.uFreq, f.freq);
    gl.uniform1f(u.uWarp, f.warp);
    gl.uniform1f(u.uWarpS, f.warpS);
    gl.uniform1f(u.uDFreq, f.dFreq);
    gl.uniform1f(u.uDetail, f.detail);
    gl.uniform1f(u.uFlow, f.flow);
    gl.uniform1f(u.uSpin, f.spin);
    gl.uniform1f(u.uZoom, ZOOM);
    gl.uniform1f(u.uRingR, f.r0 * f.ringRadius);
    gl.uniform1f(u.uRingAmp, f.ringAmp);
    gl.uniform3f(u.uOrigin, f.origin[0], f.origin[1], f.origin[2]);
    gl.uniform3f(u.uPA, f.coeffs.a[0], f.coeffs.a[1], f.coeffs.a[2]);
    gl.uniform3f(u.uPB, f.coeffs.b[0], f.coeffs.b[1], f.coeffs.b[2]);
    gl.uniform3f(u.uPC, f.coeffs.c[0], f.coeffs.c[1], f.coeffs.c[2]);
    gl.uniform3f(u.uPD, f.coeffs.d[0], f.coeffs.d[1], f.coeffs.d[2]);
    gl.uniform3f(u.uAccent, f.accent[0], f.accent[1], f.accent[2]);

    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }

  destroy(): void {
    const gl = this.gl;
    if (this.prog) gl.deleteProgram(this.prog);
    if (this.vao) gl.deleteVertexArray(this.vao);
    this.prog = null;
    this.vao = null;
    gl.getExtension("WEBGL_lose_context")?.loseContext();
  }
}

// ---------------------------------------------------------------------------
// Tier 2: 2D canvas
//
// The same silhouette field as the shader, sampled on a circle rather than on a
// plane. Sampling noise over uv directly leaves a seam at theta = +/-pi and the
// outline visibly swims; sampling on a ring is inherently periodic.
// ---------------------------------------------------------------------------

// Ashima and McEwan's 2D simplex noise, ported to scalar JS. Same kernel as the
// GLSL above, so the two tiers move in the same way.
function jmod289(x: number): number {
  return x - Math.floor(x * (1 / 289)) * 289;
}
function jpermute(x: number): number {
  return jmod289((x * 34 + 10) * x);
}
function fract(x: number): number {
  return x - Math.floor(x);
}

const C0 = 0.211324865405187;
const C1 = 0.366025403784439;
const C3 = 0.024390243902439;

function corner(p: number, dx: number, dy: number): number {
  let m = 0.5 - (dx * dx + dy * dy);
  if (m <= 0) return 0;
  m *= m;
  m *= m;
  const x = 2 * fract(p * C3) - 1;
  const h = Math.abs(x) - 0.5;
  const a0 = x - Math.floor(x + 0.5);
  m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);
  return m * (a0 * dx + h * dy);
}

function snoise2(x: number, y: number): number {
  const s = (x + y) * C1;
  let i = Math.floor(x + s);
  let j = Math.floor(y + s);
  const t = (i + j) * C0;
  const x0 = x - i + t;
  const y0 = y - j + t;
  const ix = x0 > y0 ? 1 : 0;
  const iy = x0 > y0 ? 0 : 1;
  i = jmod289(i);
  j = jmod289(j);
  const p0 = jpermute(jpermute(j) + i);
  const p1 = jpermute(jpermute(j + iy) + i + ix);
  const p2 = jpermute(jpermute(j + 1) + i + 1);
  return (
    130 *
    (corner(p0, x0, y0) +
      corner(p1, x0 - ix + C0, y0 - iy + C0) +
      corner(p2, x0 - 1 + 2 * C0, y0 - 1 + 2 * C0))
  );
}

function fbm2(x: number, y: number, oct: number): number {
  let f = 0;
  let a = 0.55;
  let n = 0;
  for (let i = 0; i < oct; i++) {
    f += a * snoise2(x, y);
    n += a;
    const nx = (0.8 * x + 0.6 * y) * 2.03;
    const ny = (-0.6 * x + 0.8 * y) * 2.03;
    x = nx;
    y = ny;
    a *= 0.45;
  }
  return f / Math.max(n, 1e-4);
}

const RING_POINTS = 128;

class Canvas2dPainter implements Painter {
  private w = 0;
  private h = 0;

  constructor(private readonly ctx: CanvasRenderingContext2D) {}

  resize(w: number, h: number): void {
    this.w = w;
    this.h = h;
  }

  paint(f: BlobFrame): void {
    const { ctx, w, h } = this;
    if (w === 0 || h === 0) return;
    ctx.clearRect(0, 0, w, h);

    const cx = w / 2;
    const cy = h / 2;
    const rPix = screenRadius(f.r0) * Math.min(w, h) * 0.5;
    const t = f.time;

    ctx.beginPath();
    for (let k = 0; k < RING_POINTS; k++) {
      const a = (k / RING_POINTS) * Math.PI * 2;
      const rx = Math.cos(a) * f.freq + f.origin[0];
      const ry = Math.sin(a) * f.freq + f.origin[1];
      const wx = snoise2(rx * 0.6, ry * 0.6 + t * 0.11);
      const wy = snoise2(rx * 0.6 + 5.2, ry * 0.6 + 1.3 + t * 0.13);
      const sil = fbm2(rx + f.warpS * 0.4 * wx, ry + f.warpS * 0.4 * wy + t * f.flow, 2);
      const r = rPix * (1 + f.ampFrac * sil);
      const px = cx + Math.cos(a) * r;
      const py = cy + Math.sin(a) * r;
      if (k === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();

    // The design's two-stop gradient: the state colour, and a 6% lighter version
    // of itself, keyed from the upper left so the disc reads as a body.
    const grad = ctx.createRadialGradient(
      cx - rPix * 0.3,
      cy - rPix * 0.34,
      rPix * 0.04,
      cx,
      cy,
      rPix,
    );
    grad.addColorStop(0, paletteCss(f.coeffs, 0.06));
    grad.addColorStop(1, paletteCss(f.coeffs));
    ctx.fillStyle = grad;
    ctx.fill();

    if (f.ringAmp > 1e-6) {
      // ringAmp is linear light; the stroke is a display-space opacity.
      ctx.globalAlpha = Math.min(Math.pow(f.ringAmp, 1 / 2.2), 1);
      ctx.strokeStyle = linearToCss(f.accent);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(cx, cy, rPix * f.ringRadius, 0, Math.PI * 2);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
  }

  destroy(): void {
    this.ctx.clearRect(0, 0, this.w, this.h);
  }
}

// ---------------------------------------------------------------------------

/**
 * The one blob on the page, and the one WebGL2 context, kept across mounts.
 *
 * There is a single blob in this product — it is the same character on the landing
 * as it is in a conversation — but every component that showed it built its own
 * renderer, and `App` gives `Stage` `key={conversation.id}`, so opening another
 * conversation remounted the stage and threw the context away: five contexts over
 * six switches, each one a fresh shader compile. The compile itself is async in the
 * driver, but reading its status is not, and that read blocks the main thread —
 * 6ms measured here on a warm discrete GPU, commonly 50-200ms on the integrated
 * GPUs this raymarched shader was tuned for, once per conversation opened.
 *
 * The canvas element is what owns a context, so the canvas is what has to outlive
 * the mount. A caller borrows it, parents it, and hands it back on unmount; the
 * context, the program and the VAO are built once per page load. Nothing else about
 * the mount changes — the stage is still remounted per conversation, so every piece
 * of per-conversation state is still seeded from scratch.
 */
let shared: { canvas: HTMLCanvasElement; renderer: BlobRenderer } | null = null;

export function acquireBlob(): { canvas: HTMLCanvasElement; renderer: BlobRenderer } {
  if (shared === null) {
    const canvas = document.createElement("canvas");
    canvas.setAttribute("aria-hidden", "true");
    shared = { canvas, renderer: new BlobRenderer(canvas) };
  }
  return shared;
}

export class BlobRenderer {
  private readonly motion = new BlobMotion();
  private painter: Painter;
  private webgl: WebglPainter | null = null;
  private observer: ResizeObserver | null = null;

  private raf = 0;
  private running = false;
  private last = 0;
  private w = 0;
  private h = 0;
  private renderScale = 1;
  private frameAvg = 16;
  private slowFrames = 0;

  private contextLost = false;
  private lostTimer = 0;

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.painter = this.selectTier();
    this.observeSize();
    document.addEventListener("visibilitychange", this.onVisibility);
  }

  // -- public API ----------------------------------------------------------

  setState(s: BlobState): void {
    this.motion.setState(s);
    this.wake();
  }

  setLevel(v: number): void {
    this.motion.setLevel(v);
    this.wake();
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.last = performance.now() / 1000;
    this.wake();
  }

  stop(): void {
    this.running = false;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  destroy(): void {
    this.stop();
    if (this.lostTimer) clearTimeout(this.lostTimer);
    this.lostTimer = 0;
    document.removeEventListener("visibilitychange", this.onVisibility);
    this.canvas.removeEventListener("webglcontextlost", this.onContextLost);
    this.canvas.removeEventListener("webglcontextrestored", this.onContextRestored);
    this.observer?.disconnect();
    this.observer = null;
    this.painter.destroy();
  }

  // -- tiers ---------------------------------------------------------------

  private selectTier(): Painter {
    // Once a canvas has handed out a WebGL context it can never hand out a 2D
    // one, so the choice between tier 1 and tier 2 has to be made here, before
    // any context exists. A context lost later falls through to CSS instead.
    try {
      const gl = this.canvas.getContext("webgl2", {
        alpha: true,
        antialias: false, // the silhouette is antialiased analytically
        depth: false,
        stencil: false,
        premultipliedAlpha: true,
        powerPreference: "high-performance",
        desynchronized: true,
      });
      if (gl) {
        this.canvas.addEventListener("webglcontextlost", this.onContextLost);
        this.canvas.addEventListener("webglcontextrestored", this.onContextRestored);
        const painter = new WebglPainter(gl);
        this.webgl = painter;
        return painter;
      }
    } catch {
      // Blocklisted driver, a context the compiler rejected, or no WebGL2 at
      // all. All of them mean the same thing: use a cheaper tier.
    }

    try {
      const ctx = this.canvas.getContext("2d", { alpha: true });
      if (ctx) return new Canvas2dPainter(ctx);
    } catch {
      // Fall through.
    }

    return new CssBlob(this.canvas);
  }

  private dropToCss(): void {
    if (this.painter instanceof CssBlob) return;
    this.painter.destroy();
    this.webgl = null;
    this.painter = new CssBlob(this.canvas);
    // The CSS tier has no GL context left to lose, so the loop is allowed to run
    // again. Without this the blob would sit on its last colour forever.
    this.contextLost = false;
    this.wake();

    // Chrome goes on compositing the last frame a lost context produced, even
    // after the drawing buffer is resized, so the CSS disc would appear behind a
    // frozen picture of the old blob. Toggling visibility drops that layer and
    // costs nothing in layout, because the element keeps its box.
    const visibility = this.canvas.style.visibility;
    this.canvas.style.visibility = "hidden";
    // Whichever comes first. rAF does not run while the document is hidden, and a
    // backgrounded tab is precisely when a GPU context is most likely to be taken
    // away — so on rAF alone the blob stayed invisible until the tab came back.
    const restore = (): void => {
      this.canvas.style.visibility = visibility;
    };
    requestAnimationFrame(() => requestAnimationFrame(restore));
    setTimeout(restore, 100);
  }

  // -- context loss --------------------------------------------------------

  private onContextLost = (e: Event) => {
    // Context loss is an expected state on integrated graphics, not an error.
    // preventDefault is what makes a restore possible at all.
    e.preventDefault();
    this.contextLost = true;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    // Come back smaller: whatever killed the context will kill it again at the
    // same size.
    this.renderScale = Math.max(0.55, this.renderScale * 0.7);
    this.forceResize();
    if (this.lostTimer) clearTimeout(this.lostTimer);
    this.lostTimer = window.setTimeout(() => {
      this.lostTimer = 0;
      if (this.contextLost) this.dropToCss();
    }, 3000);
  };

  private onContextRestored = () => {
    this.contextLost = false;
    if (this.lostTimer) clearTimeout(this.lostTimer);
    this.lostTimer = 0;
    try {
      this.webgl?.build();
      // A restored context comes back with a fresh, unset viewport.
      this.forceResize();
      this.wake();
    } catch {
      this.dropToCss();
    }
  };

  // -- sizing --------------------------------------------------------------

  private observeSize(): void {
    const c = this.canvas;
    const ro = new ResizeObserver((entries) => {
      const e = entries[0];
      if (!e) return;
      const box = e.devicePixelContentBoxSize?.[0];
      if (box) {
        // Exact device pixels, measured after layout and before paint — no
        // rounding drift from contentBox * devicePixelRatio.
        this.resize(box.inlineSize, box.blockSize);
      } else {
        // Cap DPR at 2: DPR 3 costs 2.25x for no visible gain on a soft body.
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        this.resize(c.clientWidth * dpr, c.clientHeight * dpr);
      }
    });
    // device-pixel-content-box THROWS where it is unsupported rather than being
    // ignored, so the fallback observe has to sit in the catch.
    try {
      ro.observe(c, { box: "device-pixel-content-box" });
    } catch {
      ro.observe(c);
    }
    this.observer = ro;
  }

  private resize(devW: number, devH: number): void {
    const cap = MAX_DEVICE_PX * this.renderScale;
    const k = Math.min(1, cap / Math.max(devW, devH, 1));
    const w = Math.max(1, Math.round(devW * k));
    const h = Math.max(1, Math.round(devH * k));
    if (w === this.w && h === this.h) return;
    this.w = w;
    this.h = h;
    // Only the drawing buffer changes; the CSS size is untouched and the
    // browser upscales, which is free extra antialiasing.
    this.canvas.width = w;
    this.canvas.height = h;
    this.painter.resize(w, h);
    this.wake();
  }

  /** Re-measure from the element, ignoring the cached size. */
  private forceResize(): void {
    this.w = this.h = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.resize(this.canvas.clientWidth * dpr, this.canvas.clientHeight * dpr);
  }

  // -- loop ----------------------------------------------------------------

  private onVisibility = () => {
    if (document.hidden) {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = 0;
    } else {
      this.last = performance.now() / 1000;
      this.wake();
    }
  };

  private wake(): void {
    if (!this.running || this.raf || this.contextLost || document.hidden) return;
    this.raf = requestAnimationFrame(this.frame);
  }

  private frame = (now: number) => {
    this.raf = 0;
    const t0 = performance.now();
    const seconds = now / 1000;
    const dt = Math.min(Math.max(seconds - this.last, 0), 1 / 20);
    this.last = seconds;

    const f = this.motion.step(dt);
    this.painter.paint(f);

    // Parked, not stopped: with motion suppressed, or on the CSS tier where the
    // stylesheet carries the animation, a settled blob has nothing left to draw.
    // setState and setLevel wake it again.
    const staticTier = this.motion.reduced || this.painter instanceof CssBlob;
    if (staticTier && this.motion.settled) return;

    this.adaptQuality(performance.now() - t0);
    this.wake();
  };

  private adaptQuality(frameMs: number): void {
    this.frameAvg = this.frameAvg * 0.9 + frameMs * 0.1;
    if (this.frameAvg <= 20) {
      this.slowFrames = 0;
      return;
    }
    if (++this.slowFrames <= 30) return;
    this.slowFrames = 0;
    // Cost scales with pixels, not with march steps, so shrink the buffer first
    // and only drop the step count once there is nowhere left to shrink to.
    if (this.renderScale > 0.55) {
      this.renderScale = Math.max(0.55, this.renderScale - 0.15);
      this.forceResize();
    } else {
      this.webgl?.setQuality(true);
    }
  }
}
